/**
 * Hook for Monday.com SDK context
 * 
 * Gets the current item ID and board ID from the Monday.com SDK.
 * Supports two modes:
 * - Item View: Shows photos for a specific listing (uses itemId)
 * - Board View: Shows brand assets (uses special 'brand-assets' ID)
 */

import { useState, useEffect } from 'react';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

// Special ID for brand assets (not tied to any listing)
export const BRAND_ASSETS_ID = 'brand-assets';

export interface MondayContext {
  itemId: string | null;
  boardId: string | null;
  isBrandMode: boolean;  // True when viewing brand assets (Board View)
  loading: boolean;
  error: string | null;
}

/**
 * Get the Monday.com context (item ID, board ID) from the SDK
 */
export function useMondayContext(): MondayContext {
  const [context, setContext] = useState<MondayContext>({
    itemId: null,
    boardId: null,
    isBrandMode: false,
    loading: true,
    error: null,
  });

  useEffect(() => {
    // Listen for context from Monday SDK
    const unsubscribe = monday.listen('context', (res: { data: { 
      itemId?: number | string; 
      boardId?: number | string;
    } }) => {
      const data = res.data;
      
      const itemId = data.itemId ? String(data.itemId) : null;
      const boardId = data.boardId ? String(data.boardId) : null;
      
      // If no itemId but we have boardId, we're in Board View (brand assets mode)
      if (!itemId && boardId) {
        setContext({
          itemId: BRAND_ASSETS_ID,
          boardId,
          isBrandMode: true,
          loading: false,
          error: null,
        });
        return;
      }
      
      // If no itemId and no boardId, something is wrong
      if (!itemId) {
        setContext({
          itemId: null,
          boardId: null,
          isBrandMode: false,
          loading: false,
          error: 'No item selected. Please open this from a listing item or board view.',
        });
        return;
      }

      // Normal Item View mode
      setContext({
        itemId,
        boardId,
        isBrandMode: false,
        loading: false,
        error: null,
      });
    }) as (() => void) | undefined;

    // Timeout fallback for development/testing
    const timeout = setTimeout(() => {
      setContext((prev) => {
        if (prev.loading) {
          // In development, use test params from URL
          const params = new URLSearchParams(window.location.search);
          const testItemId = params.get('itemId');
          const brandMode = params.get('brand') === 'true';
          
          // Test brand mode
          if (brandMode) {
            return {
              itemId: BRAND_ASSETS_ID,
              boardId: 'test-board',
              isBrandMode: true,
              loading: false,
              error: null,
            };
          }
          
          // Test item mode
          if (testItemId) {
            return {
              itemId: testItemId,
              boardId: null,
              isBrandMode: false,
              loading: false,
              error: null,
            };
          }
          
          return {
            ...prev,
            loading: false,
            error: 'Could not connect to Monday.com. Make sure you are running inside Monday.',
          };
        }
        return prev;
      });
    }, 3000);

    return () => {
      clearTimeout(timeout);
      // Cleanup Monday SDK listener if available
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  return context;
}
